import cx from 'classnames';
import { History, Location } from 'history';
import {
    FORUM_COMMENT_REMOVE_COMPLAINTS,
} from 'mk/autogenerated/translations/Comment.9cd56ef7351e3707592678245e3f78b9'
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import CommentBody from 'mk2/apps/blogs/components/CommentBody';
import CommentFooter from 'mk2/apps/blogs/components/CommentFooter';
import { CommentHeader } from 'mk2/apps/blogs/components/CommentHeader';
import CommentMenu from 'mk2/apps/blogs/components/CommentMenu';
import CommentRepliedToMessage from 'mk2/apps/blogs/components/CommentRepliedToMessage';
import { userHasAdminPerm } from 'mk2/apps/blogs/utils';
import { CounsellingCounsellorEntity, ForumMessageEntity, PostEntity } from 'mk2/apps/forum/schemas';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { Panel } from 'mk2/components/Panel';
import { UntrustworthyUserAlert } from 'mk2/components/UntrustworthyUserAlert';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import Complaints from 'mk2/containers/Complaints/Complaints';
import { astToHtml, defaultFilterAdditionalData } from 'mk2/helpers/article_ast/filters';
import { BlogPostEntity, CommentEntity, UserEntity } from 'mk2/schemas';
import React from 'react';
import styles from './Comment.mscss';

interface OwnState {
    hasHover: boolean;
    collapsed: boolean;
}

interface OwnProps {
    className?: string;
    comment: CommentEntity;
    highlightTriggered: boolean;
    highlightStarted?: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
    inForum: boolean;
    inGroups?: boolean;
    followLinks?: boolean;
    moderators?: UserEntity[];
    counsellor?: CounsellingCounsellorEntity;
    location: Location;
    history: History;
    post: BlogPostEntity | PostEntity;
    isMyAnonymousPost?: boolean;
    isMyAnonymousComment?: boolean;
    isSpider?: boolean;
    hasBodyHiddenText: boolean;
    hasBodyDeletedStatus: boolean;
    hasFooterLike: boolean;
    hasFooterLikeDisabled?: boolean;
    hasFooterReply: boolean;
    hasMenu: boolean;
    hasMenuReport: boolean;
    hasMenuEdit: boolean;
    hasMenuDelete: boolean;
    hasMenuMoveUp: boolean;

    showUntrustwortyUserAlert?: boolean;

    // Used to display navigation to comment target in the FeedUntrustworthyUser
    afterBody?: React.ReactNode;

    onReply(username: string, repliedToCommentId: number);
    onDelete(commentId: number);
    onReport(entityType: ComplaintTargetType, commentId: number);
    onToggleMenuMore?(commentId: number);
    onTrackEdit?(commentId: number);
    onMoveUp?(commentId: number);
    onSendHeart();
    onDidHighlight?(commentId: number);
}

type Props = OwnProps;

export default class Comment extends React.PureComponent<Props, OwnState> {

    public static defaultProps: Partial<OwnProps> = {
        showUntrustwortyUserAlert: true,
        highlightStarted: false,
    };

    private container: HTMLDivElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            hasHover: false,
            collapsed: true,
        };
    }

    public componentDidMount() {
        const {
            comment, highlightTriggered, inForum, isMobile, onDidHighlight, onTrackEdit,
        } = this.props;

        if (highlightTriggered) {
            this.container.scrollIntoView(true);
            if (window && isMobile) {
                // scroll 44px of sticky mobile header
                window.scrollBy(0, -44);
            }
            if (onDidHighlight) {
                onDidHighlight(comment.id);
            }
        }
        if (inForum && this.isEditable()) {
            onTrackEdit(comment.id);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { comment, highlightTriggered, isMobile, onDidHighlight } = this.props;
        if (highlightTriggered && !prevProps.highlightTriggered) {
            this.container.scrollIntoView(true);
            if (window && isMobile) {
                // scroll 44px of sticky mobile header
                window.scrollBy(0, -44);
            }
            if (onDidHighlight) {
                onDidHighlight(comment.id);
            }
        }
    }

    public render() {
        const {
            comment, followLinks, inForum, inGroups, highlightStarted,
            hasBodyHiddenText, hasBodyDeletedStatus,
            hasFooterLike, hasFooterLikeDisabled, hasFooterReply, hasMenuReport, hasMenuEdit, hasMenuDelete,
            requestUser, requestUserPermissions, moderators, isMobile, location,
            onReply, onDelete, onReport, onToggleMenuMore, onMoveUp, hasMenuMoveUp, hasMenu,
            onSendHeart, counsellor, className, showUntrustwortyUserAlert,
            afterBody, post, isMyAnonymousComment, history,
        } = this.props;

        const inBlogs = !(inForum || inGroups);
        const isAdmin = userHasAdminPerm(inBlogs, requestUserPermissions);

        const isModerator = (inForum || inGroups) && moderators && isAuthenticatedAs(requestUser, moderators);
        const isOwner = isAuthenticatedAs(requestUser, comment.authorUser) || isMyAnonymousComment;
        const isSalesAdmin = requestUserPermissions.indexOf('bo.can_manage_sales') >= 0;
        const isGroupPost = post && !!((post as BlogPostEntity).group);

        const hasAdminclub = isAdmin && inForum;
        const hasDelete = hasMenuDelete && (
            inGroups
                ? isOwner || isModerator || isAdmin || (!isGroupPost && comment.canDelete)
                : hasMenuDelete
        );

        const hasEdit = hasMenuEdit && (
            isAdmin || (isOwner && this.isEditable())
        );

        let role = null;
        if (isOwner) {
            role = 'author';
        } else if (isModerator) {
            role = 'moderator';
        } else if (isAdmin) {
            role = 'admin';
        }

        const showComplaints = (inForum || inGroups) && !comment.deletedAt && (isAdmin || isModerator);
        const isCollapsedIfDeleted = this.state.collapsed;

        const isPostAuthor = comment.isPostAuthor;  // TODO remove unnecessary variable

        // const repliedToBlogComment = !inForum ? comment.repliedToComment as CommentEntity : undefined;
        const repliedToForumMessage = inForum ? comment.repliedToComment as ForumMessageEntity : undefined;
        return (
            <div
                id={`comment-${comment.id}`}
                className={cx(
                    styles.Comment, className,
                    inBlogs && styles['Comment--inBlogs'],
                    inForum && styles['Comment--inForum'],
                    inGroups && styles['Comment--inGroups'],
                    highlightStarted && styles['Comment--highlighted'],
                    (!inGroups) && comment.deletedAt && isCollapsedIfDeleted && styles['Comment--deletedCollapsed'],
                    (!inGroups) && comment.deletedAt && !isCollapsedIfDeleted && styles['Comment--deletedExpanded'],
                    !comment.deletedAt && !hasFooterLike && !hasFooterReply && styles['Comment--noFooter'],
                    inForum && comment.byAdmin && styles['Comment--inForum--admin'],
                )}
                ref={this.handleConteinterRef}
                onMouseEnter={this.handleOnMouseEnter}
                onMouseLeave={this.handleOnMouseLeave}
            >
                {inGroups ? (
                    <div className={styles.Comment__blogsLayout}>
                        {!comment.byAdmin && (
                            <div className={styles.Comment__blogsLayoutCol1}>
                                <CommentHeader
                                    className={cx(
                                        styles.Comment__header,
                                        comment.deletedAt && isCollapsedIfDeleted && styles['Comment__header--deletedCollapsed'],
                                    )}
                                    comment={comment}
                                    isPostAuthor={isPostAuthor}
                                    isMyAnonymousComment={isMyAnonymousComment}
                                    isCollapsed={isCollapsedIfDeleted}
                                    inForum={false}
                                    inGroups={inGroups}
                                    moderators={moderators}
                                    onToggleDeletedDetails={this.handleOnToggleDeletedDetails}
                                />
                            </div>
                        )}
                        <div className={styles.Comment__blogsLayoutCol2}>
                            <CommentBody
                                comment={comment}
                                post={post}
                                isAdmin={isAdmin}
                                isModerator={isModerator}
                                isPostAuthor={isPostAuthor}
                                isMyAnonymousComment={isMyAnonymousComment}
                                isCollapsed={isCollapsedIfDeleted}
                                inForum={false}
                                inGroups={inGroups}
                                followLinks={followLinks}
                                hasHiddenText={hasBodyHiddenText}
                                hasDeletedStatus={hasBodyDeletedStatus}
                                moderators={moderators}
                                onToggleDeletedDetails={this.handleOnToggleDeletedDetails}
                                history={history}
                            />
                            <div className={styles.Comment__footerContainer}>
                                <CommentFooter
                                    className={styles.Comment__footer}
                                    comment={comment}
                                    inForum={false}
                                    inGroups={inGroups}
                                    isCollapsed={isCollapsedIfDeleted}
                                    requestUser={requestUser}
                                    hasDelete={false}
                                    hasEdit={false}
                                    hasId={(isAdmin || isSalesAdmin) && this.state.hasHover}
                                    hasLike={hasFooterLike}
                                    hasLikeDisabled={hasFooterLikeDisabled}
                                    hasReply={hasFooterReply}
                                    hasReport={false}
                                    onDelete={onDelete}
                                    onReply={onReply}
                                    onReport={onReport}
                                    location={location}
                                />

                                {hasMenu && (
                                    <CommentMenu
                                        className={styles.Comment__dropdownMenu}
                                        requestUser={requestUser}
                                        comment={comment}
                                        inGroups={inGroups}
                                        isGroupPost={isGroupPost}
                                        role={role}
                                        location={location}
                                        hasAdminclub={hasAdminclub}
                                        hasReport={hasMenuReport}
                                        hasDelete={hasDelete}
                                        hasEdit={hasEdit}
                                        onToggleMenuMore={onToggleMenuMore}
                                        onReport={onReport}
                                        onDelete={onDelete}
                                        onMoveUp={onMoveUp}
                                        hasMoveUp={hasMenuMoveUp && requestUserPermissions.indexOf('forum.can_manage_forum') && (comment.positionInPost > 1)}
                                        onSendHeart={onSendHeart}
                                        onSetCollapsedFalse={this.handleOnSetCollapsedFalse}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                ) : inForum ? (
                    <div className={styles.Comments__forumLayout}>
                        <CommentHeader
                            className={cx(
                                styles.Comment__header,
                                comment.deletedAt && isCollapsedIfDeleted && styles['Comment__header--deletedCollapsed'],
                            )}
                            comment={comment}
                            isPostAuthor={isPostAuthor}
                            isMyAnonymousComment={isMyAnonymousComment}
                            isCollapsed={isCollapsedIfDeleted}
                            inForum={inForum}
                            inGroups={inGroups}
                            moderators={moderators}
                            onToggleDeletedDetails={this.handleOnToggleDeletedDetails}
                            counsellor={counsellor}
                        />

                        {hasMenu &&
                            <CommentMenu
                                className={styles.Comment__dropdownMenu}
                                requestUser={requestUser}
                                comment={comment}
                                inGroups={inGroups}
                                role={role}
                                location={location}
                                hasAdminclub={hasAdminclub}
                                hasReport={hasMenuReport}
                                hasDelete={hasDelete}
                                hasEdit={hasEdit}
                                onToggleMenuMore={onToggleMenuMore}
                                onReport={onReport}
                                onDelete={onDelete}
                                onMoveUp={onMoveUp}
                                hasMoveUp={hasMenuMoveUp && requestUserPermissions.indexOf('forum.can_manage_forum') && (comment.positionInPost > 1)}
                                onSendHeart={onSendHeart}
                                onSetCollapsedFalse={this.handleOnSetCollapsedFalse}
                            />
                        }

                        {showUntrustwortyUserAlert && comment.authorUser.isUntrustworthy && (!comment.deletedAt || !isCollapsedIfDeleted)
                            ? <UntrustworthyUserAlert className={styles.Comment__untrustworthyUser} user={comment.authorUser} />
                            : null}

                        {(
                            (comment.repliedToComment) &&
                            (
                                !comment.deletedAt ||
                                comment.deletedAt && !isCollapsedIfDeleted
                            )
                        ) && (
                            <div className={styles.Comment__repliedToMessageWrapper}>
                                <CommentRepliedToMessage
                                     clamp={2}
                                     style={'light'}
                                     repliedToForumMessage={repliedToForumMessage}
                                     forumPost={post as PostEntity}
                                     history={history}
                                />
                            </div>
                        )}
                        <CommentBody
                            comment={comment}
                            post={post}
                            isAdmin={isAdmin}
                            isModerator={isModerator}
                            isPostAuthor={isPostAuthor}
                            isMyAnonymousComment={isMyAnonymousComment}
                            isCollapsed={isCollapsedIfDeleted}
                            inForum={inForum}
                            inGroups={inGroups}
                            followLinks={followLinks}
                            hasHiddenText={hasBodyHiddenText}
                            hasDeletedStatus={hasBodyDeletedStatus}
                            moderators={moderators}
                            history={history}
                        />
                        {counsellor?.signatureAST && (
                            <div className={styles.Comment__signature}>
                                <br/>
                                {astToHtml(counsellor.signatureAST, {
                                    ...defaultFilterAdditionalData,
                                    renderLinksWithNofollow: false,
                                })}
                            </div>
                        )}

                        {afterBody}

                        <CommentFooter
                            className={styles.Comment__footer}
                            comment={comment}
                            inForum={inForum}
                            inGroups={inGroups}
                            isCollapsed={isCollapsedIfDeleted}
                            requestUser={requestUser}
                            hasDelete={false}
                            hasEdit={false}
                            hasId={false}
                            hasLike={hasFooterLike}
                            hasLikeDisabled={hasFooterLikeDisabled}
                            hasReply={hasFooterReply}
                            hasReport={false}
                            onDelete={onDelete}
                            onReply={onReply}
                            onReport={onReport}
                            location={location}
                        />

                        {showComplaints && (
                            <Complaints
                                id={comment.id}
                                entity={ComplaintTargetType.ForumMessage}
                                deleteLabel={FORUM_COMMENT_REMOVE_COMPLAINTS}
                            />
                        )}
                    </div>
                ) : (
                    <div className={cx(styles.Comment__blogsLayout, comment.byAdmin && styles['Comment__blogsLayout--admin'])}>
                        {!comment.byAdmin && (
                            <div className={styles.Comment__blogsLayoutCol1}>
                                <CommentHeader
                                    className={cx(
                                        styles.Comment__header,
                                        comment.deletedAt && isCollapsedIfDeleted && styles['Comment__header--deletedCollapsed'],
                                    )}
                                    comment={comment}
                                    isPostAuthor={isPostAuthor}
                                    isMyAnonymousComment={isMyAnonymousComment}
                                    isCollapsed={false}
                                    inForum={false}
                                    inGroups={false}
                                    moderators={moderators}
                                    onToggleDeletedDetails={this.handleOnToggleDeletedDetails}
                                />
                            </div>
                        )}
                        <div className={styles.Comment__blogsLayoutCol2}>
                            <CommentBody
                                comment={comment}
                                post={post}
                                isAdmin={isAdmin}
                                isModerator={isModerator}
                                isPostAuthor={isPostAuthor}
                                isMyAnonymousComment={isMyAnonymousComment}
                                isCollapsed={isCollapsedIfDeleted}
                                inForum={false}
                                inGroups={false}
                                followLinks={followLinks}
                                hasHiddenText={hasBodyHiddenText}
                                hasDeletedStatus={hasBodyDeletedStatus}
                                moderators={moderators}
                                history={history}
                            />

                            <CommentFooter
                                className={styles.Comment__footer}
                                comment={comment}
                                inForum={false}
                                inGroups={false}
                                isCollapsed={isCollapsedIfDeleted}
                                requestUser={requestUser}
                                hasDelete={this.state.hasHover || isMobile}
                                hasEdit={(this.state.hasHover || isMobile) && hasEdit}
                                hasId={(isAdmin || isSalesAdmin) && this.state.hasHover}
                                hasLike={hasFooterLike}
                                hasLikeDisabled={hasFooterLikeDisabled}
                                hasReply={this.state.hasHover || isMobile}
                                hasReport={this.state.hasHover || isMobile}
                                onDelete={onDelete}
                                onReply={onReply}
                                onReport={onReport}
                                location={location}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private isEditable = () => {
        const commentTime = new Date(this.props.comment.created);
        const commentTimeWithDelay = new Date((new Date (commentTime)).setMinutes(commentTime.getMinutes() + 2));
        const currentTime = new Date(Date.now());
        return commentTimeWithDelay > currentTime;
    };

    private handleConteinterRef = (r: HTMLDivElement) => {
        this.container = r;
    };

    private handleOnMouseEnter = () => {
        this.setState({ hasHover: true });
    };

    private handleOnMouseLeave = () => {
        this.setState({ hasHover: false });
    };

    private handleOnToggleDeletedDetails = () => {
        this.setState({ collapsed: !this.state.collapsed });
    };
    private handleOnSetCollapsedFalse = () => {
        this.setState({ collapsed: false });
    };
}

interface CommentForumPlaceholderOwnProps {
    style?: any;
    className?: string;
}

export const CommentForumPlaceholder: React.StatelessComponent<CommentForumPlaceholderOwnProps> =
    ({ className, style }) => (
    <Panel className={cx(styles.CommentForumPlaceholder, className)} style={style}>
        <div className={styles.CommentForumPlaceholder__meta}>
            <LoadingPlaceholder width="50%" height={17}/>
        </div>
        <div className={styles.CommentForumPlaceholder__body}>
            <LoadingPlaceholder width="100%" height={17}/>
            <LoadingPlaceholder width="100%" height={17}/>
        </div>
        <LoadingPlaceholder width={150}/>
    </Panel>
);
CommentForumPlaceholder.displayName = 'CommentForumPlaceholder';
